import { useEffect, useState } from "react";
import type { User, UserRole } from "@shared/core/types";
import { ROLES, ROLE_LABELS } from "@shared/core/types";
import { api, ApiError } from "../lib/api";
import { useAuthStore } from "../stores/auth";
import { useToastStore } from "../stores/toast";
import { PageHeader } from "../components/ui/page-header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Modal } from "../components/ui/modal";
import { Field, Input, Select } from "../components/ui/input";
import { Icon } from "../components/ui/icon";
import { formatDateTime } from "../lib/format";

type Draft = { email: string; name: string; role: UserRole; password: string };
const EMPTY: Draft = { email: "", name: "", role: "recrutement", password: "" };

const ERRORS: Record<string, string> = {
  email_taken: "Ce courriel est déjà utilisé.",
  last_admin: "Impossible : c'est le dernier administrateur actif.",
  cannot_delete_self: "Vous ne pouvez pas supprimer votre propre compte.",
  invalid_input: "Vérifiez les champs (mot de passe d'au moins 12 caractères).",
};

const ROLE_HINTS: Record<UserRole, string> = {
  admin: "Tout, y compris les comptes et les réglages.",
  direction: "Tout le métier, le journal et l'attribution des dossiers aux avocats.",
  recrutement: "Crée et fait avancer les dossiers, dépose et valide les pièces.",
  administration: "Lit les dossiers et suit la liste des pièces ; ne supprime rien.",
  avocat: "Ne voit que les dossiers qui lui sont confiés.",
};

export function UsersPage() {
  const me = useAuthStore((s) => s.user);
  const push = useToastStore((s) => s.push);
  const [list, setList] = useState<User[]>([]);
  const [editing, setEditing] = useState<User | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => api<User[]>("/api/users").then(setList).catch(() => setList([]));
  useEffect(() => {
    void load();
  }, []);

  const open = (target: User | "new") => {
    setEditing(target);
    setDraft(target === "new" ? EMPTY : { email: target.email, name: target.name, role: target.role, password: "" });
  };

  const fail = (e: unknown, fallback: string) => {
    const code = e instanceof ApiError ? e.code : "";
    push("danger", ERRORS[code] ?? fallback);
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (editing === "new") {
        await api("/api/users", { method: "POST", body: JSON.stringify(draft) });
        push("success", "Compte créé");
      } else if (editing) {
        const body: Partial<Draft> = { name: draft.name, role: draft.role };
        if (draft.password) body.password = draft.password;
        await api(`/api/users/${editing.id}`, { method: "PATCH", body: JSON.stringify(body) });
        push("success", "Compte mis à jour");
      }
      setEditing(null);
      await load();
    } catch (e) {
      fail(e, "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (u: User, body: Record<string, unknown>, done: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api(`/api/users/${u.id}`, { method: "PATCH", body: JSON.stringify(body) });
      push("success", done);
      await load();
    } catch (e) {
      fail(e, "Modification impossible");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete || busy) return;
    setBusy(true);
    try {
      await api(`/api/users/${confirmDelete.id}`, { method: "DELETE" });
      push("success", "Compte supprimé");
      setConfirmDelete(null);
      await load();
    } catch (e) {
      fail(e, "Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Utilisateurs"
        subtitle="Comptes qui peuvent se connecter et leur niveau d'accès."
        actions={
          <Button variant="primary" icon={<Icon name="plus" size={16} />} onClick={() => open("new")}>
            Nouveau compte
          </Button>
        }
      />

      <table className="table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Courriel</th>
            <th>Rôle</th>
            <th>2FA</th>
            <th className="table__col--wide">Dernière connexion</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.map((u) => (
            <tr key={u.id}>
              <td>
                {u.name}
                {u.active === false ? (
                  <>
                    {" "}
                    <Badge variant="danger">désactivé</Badge>
                  </>
                ) : null}
              </td>
              <td>{u.email}</td>
              <td>
                <Badge variant={u.role === "admin" ? "primary" : u.role === "avocat" ? "info" : "neutral"}>
                  {ROLE_LABELS[u.role]}
                </Badge>
              </td>
              <td>{u.mfaEnabled ? <Badge variant="success">activée</Badge> : <Badge variant="warning">non</Badge>}</td>
              <td className="table__col--wide">{formatDateTime(u.lastLoginAt ?? null, { dateStyle: "medium", timeStyle: "short" })}</td>
              <td className="table__actions">
                <Button variant="ghost" size="sm" onClick={() => open(u)}>
                  Modifier
                </Button>{" "}
                {u.mfaEnabled ? (
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => patch(u, { resetMfa: true }, "Double authentification réinitialisée")}>
                    Réinitialiser 2FA
                  </Button>
                ) : null}{" "}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={u.id === me?.id || busy}
                  onClick={() => patch(u, { active: u.active === false }, u.active === false ? "Compte réactivé" : "Compte désactivé")}
                >
                  {u.active === false ? "Réactiver" : "Désactiver"}
                </Button>{" "}
                <Button variant="ghost-danger" size="sm" disabled={u.id === me?.id} onClick={() => setConfirmDelete(u)}>
                  Supprimer
                </Button>
              </td>
            </tr>
          ))}
          {list.length === 0 ? (
            <tr>
              <td colSpan={6} className="table__empty">
                Aucun compte.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <Modal
        open={editing !== null}
        title={editing === "new" ? "Nouveau compte" : "Modifier le compte"}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Annuler
            </Button>
            <Button variant="primary" onClick={save} disabled={busy}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Field label="Nom">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
          </Field>
          <Field label="Courriel">
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              disabled={editing !== "new"}
              required
            />
          </Field>
          <Field label="Rôle" hint={ROLE_HINTS[draft.role]}>
            <Select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as UserRole })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label={editing === "new" ? "Mot de passe" : "Nouveau mot de passe"}
            hint={editing === "new" ? "Au moins 12 caractères. L'utilisateur devra activer la double authentification." : "Laisser vide pour ne pas le changer."}
          >
            <Input
              type="password"
              value={draft.password}
              onChange={(e) => setDraft({ ...draft, password: e.target.value })}
              minLength={12}
              autoComplete="new-password"
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={confirmDelete !== null}
        title="Supprimer ce compte ?"
        onClose={() => setConfirmDelete(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Annuler
            </Button>
            <Button variant="danger" onClick={remove} disabled={busy}>
              Supprimer
            </Button>
          </>
        }
      >
        <p>
          {confirmDelete?.name} ({confirmDelete?.email}) ne pourra plus se connecter. Préférez la désactivation si
          l'historique doit rester lisible. Cette action est définitive.
        </p>
      </Modal>
    </div>
  );
}
